import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { getImportSchemas, previewImport, commitImport, getImportHistory, importSampleCsvUrl, downloadImportErrors } from '../lib/api';
import { UploadSimple, FileCsv, CheckCircle, WarningCircle, ClockCounterClockwise, Trash, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function CsvImport() {
  const [searchParams] = useSearchParams();
  const initialEntity = searchParams.get('entity') || 'skus';
  const [schemas, setSchemas] = useState([]);
  const [entity, setEntity] = useState(initialEntity);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  useEffect(() => {
    getImportSchemas().then(r => {
      setSchemas(r.data);
    }).catch(() => toast.error('Failed to load schemas'));
    refreshHistory();
  }, []);

  const refreshHistory = async () => {
    try {
      const r = await getImportHistory(10);
      setHistory(r.data);
    } catch (e) { /* ignore */ }
  };

  const schema = schemas.find(s => s.entity === entity);

  const handleFile = (e) => {
    setFile(e.target.files[0]);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Choose a CSV file first');
      return;
    }
    setPreviewing(true);
    try {
      const r = await previewImport(entity, file);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const r = await commitImport(entity, file, skipDuplicates);
      const upd = r.data.updated ? ` · Updated ${r.data.updated}` : '';
      toast.success(`Inserted ${r.data.inserted}${upd} · Skipped ${r.data.skipped_duplicate} dupes · ${r.data.skipped_error} errors`);
      setFile(null);
      setPreview(null);
      refreshHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  };

  const downloadTemplate = (prefill = false) => {
    if (!schema) return;
    const url = importSampleCsvUrl(entity, prefill);
    // Let the browser handle it so the JWT cookie is sent with the request.
    window.open(url, '_blank');
  };

  const [downloadingErrors, setDownloadingErrors] = useState(false);
  const handleDownloadErrors = async () => {
    if (!file) return;
    setDownloadingErrors(true);
    try {
      const r = await downloadImportErrors(entity, file);
      const count = parseInt(r.headers?.['x-error-row-count'] || '0', 10);
      if (!count) {
        toast.success('No error rows to download — all rows passed validation');
        return;
      }
      // Prefer the filename the backend suggested so the download is self-describing.
      const dispo = r.headers?.['content-disposition'] || '';
      const match = dispo.match(/filename="?([^"]+)"?/i);
      const fname = match?.[1] || `errors_${entity}.csv`;
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${count} error row${count === 1 ? '' : 's'} — fix in place and re-upload`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    } finally {
      setDownloadingErrors(false);
    }
  };

  return (
    <Layout>
      <div data-testid="csv-import-page" className="p-6 max-w-5xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <UploadSimple size={32} weight="fill" className="text-brand" />
            Batch Import Wizard
          </h1>
          <p className="text-muted-foreground">Bulk-import SKUs, locations, fixed locations and orders from CSV</p>
        </div>

        <Tabs defaultValue="upload" className="bg-card border border-border rounded-lg">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none">
            <TabsTrigger value="upload" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              <UploadSimple size={16} className="mr-2" /> Upload
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              <ClockCounterClockwise size={16} className="mr-2" /> History ({history.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="p-5 m-0 space-y-5">
            {/* Step 1: Entity picker */}
            <div>
              <label className="overline block mb-2">Step 1 · What are you importing?</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {schemas.map(s => (
                  <button
                    key={s.entity}
                    onClick={() => { setEntity(s.entity); setFile(null); setPreview(null); }}
                    data-testid={`entity-${s.entity}`}
                    className={`border p-3 text-left transition ${entity === s.entity ? 'bg-brand text-white border-brand' : 'bg-card border-border rounded-lg hover:bg-muted'}`}
                  >
                    <p className="font-bold font-mono uppercase">{s.entity}</p>
                    <p className={`text-xs mt-1 ${entity === s.entity ? 'text-white/80' : 'text-muted-foreground'}`}>{s.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Schema + template */}
            {schema && (
              <div className="border border-border bg-muted p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-sm">Required columns</p>
                  <div className="flex items-center gap-3">
                    <button onClick={() => downloadTemplate(false)} data-testid="download-template-btn" className="text-xs text-brand font-bold uppercase">
                      <FileCsv size={12} className="inline mr-1" /> Download template
                    </button>
                    {entity === 'skus' && (
                      <button
                        onClick={() => downloadTemplate(true)}
                        data-testid="download-prefilled-btn"
                        title="Download a CSV pre-filled with every existing SKU and its current weight/volume — paste in dimensions and re-upload."
                        className="text-xs text-emerald-700 font-bold uppercase"
                      >
                        <FileCsv size={12} className="inline mr-1" /> Pre-filled SKUs
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {schema.required.map(f => (
                    <span key={f} className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-mono font-bold">* {f}</span>
                  ))}
                </div>
                {schema.optional.length > 0 && (
                  <>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">Optional</p>
                    <div className="flex flex-wrap gap-1">
                      {schema.optional.map(f => (
                        <span key={f} className="px-2 py-0.5 bg-muted text-muted-foreground text-xs font-mono">{f}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 3: File upload */}
            <div>
              <label className="overline block mb-2">Step 2 · Upload CSV</label>
              <div className="border-2 border-dashed border-border p-6 text-center">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFile}
                  className="hidden"
                  id="csv-file-input"
                  data-testid="csv-file-input"
                />
                <label htmlFor="csv-file-input" className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-brand text-brand hover:bg-brand hover:text-white font-bold uppercase text-sm">
                  <FileCsv size={16} /> Choose File
                </label>
                {file && (
                  <p className="mt-3 text-sm font-mono" data-testid="csv-selected-file">
                    <FileCsv size={14} className="inline mr-1" /> {file.name} · {(file.size / 1024).toFixed(1)} KB
                    <button onClick={() => { setFile(null); setPreview(null); }} className="ml-2 text-red-600">
                      <Trash size={12} className="inline" />
                    </button>
                  </p>
                )}
              </div>
            </div>

            {/* Step 4: Preview */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={handlePreview}
                disabled={!file || previewing}
                data-testid="preview-import-btn"
              >
                {previewing ? 'Previewing...' : 'Preview'}
              </Button>
              <Button
                onClick={handleCommit}
                disabled={!preview || committing || preview.valid_count === 0}
                className="bg-brand hover:bg-brand/90"
                data-testid="commit-import-btn"
              >
                {committing ? 'Importing...' : `Import ${preview?.valid_count || 0} rows`}
              </Button>
              <button
                onClick={() => setSkipDuplicates(!skipDuplicates)}
                className={`px-3 py-2 border text-xs font-bold uppercase ${skipDuplicates ? 'bg-blue-50 border-brand text-brand' : 'bg-card border-border rounded-lg text-muted-foreground'}`}
                data-testid="skip-duplicates-toggle"
              >
                {skipDuplicates ? '✓ Skip duplicates' : (entity === 'fixed_locations' ? 'Update existing (min/max + zone)' : 'Allow duplicates')}
              </button>
            </div>

            {/* Preview result */}
            {preview && (
              <div data-testid="import-preview-result" className="border border-border bg-card">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-border">
                  <Stat label="Total Rows" value={preview.total_rows} />
                  <Stat label="Valid" value={preview.valid_count} color="text-green-700" />
                  <Stat label="Errors" value={preview.error_count} color={preview.error_count > 0 ? "text-red-700" : ""} />
                  <Stat label="Dupes" value={preview.duplicates_in_db_count} color={preview.duplicates_in_db_count > 0 ? "text-amber-700" : ""} />
                </div>
                {preview.errors?.length > 0 && (
                  <div className="p-3 border-b border-border">
                    <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                      <p className="overline text-red-700"><WarningCircle size={12} className="inline" /> Validation Errors ({preview.error_count})</p>
                      <button
                        onClick={handleDownloadErrors}
                        disabled={downloadingErrors}
                        data-testid="download-errors-btn"
                        className="text-xs font-bold uppercase text-red-700 border border-red-300 hover:bg-red-50 disabled:opacity-50 px-2 py-1 rounded"
                        title="Download a CSV containing only the failing rows with an added _errors column so you can fix and re-upload"
                      >
                        <DownloadSimple size={12} className="inline mr-1" />
                        {downloadingErrors ? 'Preparing…' : 'Download errors.csv'}
                      </button>
                    </div>
                    <div className="max-h-32 overflow-y-auto text-xs font-mono">
                      {preview.errors.slice(0, 10).map((e, i) => (
                        <div key={i} className="py-0.5 text-muted-foreground">Row {e.row}: {e.errors.join(', ')}</div>
                      ))}
                      {preview.error_count > 10 && (
                        <div className="py-0.5 text-muted-foreground italic">… and {preview.error_count - 10} more — download the CSV above to see every row.</div>
                      )}
                    </div>
                  </div>
                )}
                {preview.sample?.length > 0 && (
                  <div className="p-3">
                    <p className="overline mb-1">Sample (first 5 rows)</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr>
                            {Object.keys(preview.sample[0]).map(k => (
                              <th key={k} className="font-mono font-bold text-muted-foreground border-b border-border px-2 py-1 text-left">{k}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.sample.map((row, i) => (
                            <tr key={i}>
                              {Object.values(row).map((v, j) => (
                                <td key={j} className="font-mono px-2 py-1 text-foreground border-b border-border">{String(v)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Entity</th>
                    <th>File</th>
                    <th>Total</th>
                    <th>Inserted</th>
                    <th>Dupes</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No import history yet</td></tr>
                  ) : history.map(r => (
                    <tr key={r.id} data-testid={`history-row-${r.id}`}>
                      <td className="text-xs text-muted-foreground">
                        {r.timestamp ? new Date(r.timestamp).toLocaleString() : '-'}
                      </td>
                      <td className="font-mono font-bold">{r.entity}</td>
                      <td className="font-mono text-xs">{r.file_name}</td>
                      <td className="font-mono">{r.total_rows}</td>
                      <td className="font-mono text-green-700 font-bold">{r.inserted}</td>
                      <td className="font-mono text-amber-700">{r.skipped_duplicate}</td>
                      <td className="font-mono text-red-700">{r.skipped_error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function Stat({ label, value, color = '' }) {
  return (
    <div className="p-3 border-r border-border last:border-r-0">
      <p className="overline">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
